import { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useImpersonation } from '@/contexts/ImpersonationContext';
import { useAuth } from '@/hooks/useAuth';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { UserRoundCog, Search } from 'lucide-react';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';

interface SelectableUser {
  id: string;
  display_name: string | null;
  first_name: string | null;
  last_name: string | null;
  email: string | null;
  avatar_url: string | null;
  system_role: { name: string; code: string } | null;
  department: { name: string } | null;
}

export function ImpersonateUserSelector() {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const { startImpersonation } = useImpersonation();
  const { user: authUser } = useAuth();

  const { data: users = [], isLoading } = useQuery({
    queryKey: ['impersonation-users'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('app_users')
        .select(`
          id, display_name, first_name, last_name, email, avatar_url,
          system_role:system_roles(name, code),
          department:departments(name)
        `)
        .eq('active', true)
        .order('first_name');
      if (error) throw error;
      return (data || []) as SelectableUser[];
    },
    enabled: open,
  });

  // Filter out current admin user
  const filteredUsers = useMemo(() => {
    let list = users.filter(u => u.id !== authUser?.id);
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter(u => {
        const name = u.display_name || [u.first_name, u.last_name].filter(Boolean).join(' ');
        return name.toLowerCase().includes(q) || (u.email?.toLowerCase().includes(q));
      });
    }
    return list;
  }, [users, search, authUser?.id]);

  const handleSelect = async (userId: string) => {
    await startImpersonation(userId);
    setOpen(false);
    setSearch('');
  };

  const getInitials = (u: SelectableUser) => {
    if (u.first_name && u.last_name) return `${u.first_name[0]}${u.last_name[0]}`.toUpperCase();
    if (u.display_name) return u.display_name.slice(0, 2).toUpperCase();
    return '??';
  };

  const getName = (u: SelectableUser) =>
    u.display_name || [u.first_name, u.last_name].filter(Boolean).join(' ') || u.email || 'Sem nome';

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" className="gap-2">
          <UserRoundCog className="h-4 w-4" />
          Impersonar
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Impersonar Usuário</DialogTitle>
        </DialogHeader>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Buscar por nome ou email..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="pl-9"
            autoFocus
          />
        </div>
        <ScrollArea className="h-[360px] -mx-2">
          {isLoading ? (
            <p className="text-center text-muted-foreground py-8 text-sm">Carregando...</p>
          ) : filteredUsers.length === 0 ? (
            <p className="text-center text-muted-foreground py-8 text-sm">Nenhum usuário encontrado</p>
          ) : (
            <div className="space-y-1 px-2">
              {filteredUsers.map(u => (
                <button
                  key={u.id}
                  onClick={() => handleSelect(u.id)}
                  className="w-full flex items-center gap-3 px-3 py-2 rounded-md hover:bg-accent text-left transition-colors"
                >
                  <Avatar className="h-8 w-8">
                    <AvatarImage src={u.avatar_url || undefined} />
                    <AvatarFallback className="text-xs">{getInitials(u)}</AvatarFallback>
                  </Avatar>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{getName(u)}</p>
                    <p className="text-xs text-muted-foreground truncate">
                      {[u.system_role?.name, u.department?.name].filter(Boolean).join(' · ') || u.email}
                    </p>
                  </div>
                </button>
              ))}
            </div>
          )}
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
}
